import React from 'react'
import utils from '../utils'
import { Helmet } from 'react-helmet';

function PolicyPage() {
    const url = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <div>
            <Helmet>
                <title>Privacy Policy - {utils.siteName}</title>
                <link rel="canonical" href={url} />
            </Helmet>
            <div>
                <header className="hero-bg text-white py-16">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl md:text-6xl font-black">Privacy Policy</h1>
                        <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">Your privacy is important to us. It is {utils.siteName} policy to respect your privacy regarding any information we may collect from you.</p>
                        <div className="mt-6 mx-auto w-20 h-1.5 bg-secondary rounded-full"></div>
                    </div>
                </header>
                <section className="py-16 lg:py-24">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 p-8 md:p-12 rounded-lg shadow-xl">
                            <div className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                                <h1 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-4">
                                    Koova Privacy Notice
                                </h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                                    Last updated: December 2025
                                </p>
                                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                                    This Privacy Notice explains how Koova (“Koova”, “we”, “us”, or “our”) collects, uses, shares, and protects your personal data when you access or use our website, mobile applications, and related services (collectively, the “Platform”).<br />
                                    By using Koova, you acknowledge that you have read and understood this Privacy Notice.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    1. About This Notice
                                </h2>
                                <p>This Privacy Notice describes:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>What personal data we collect</li>
                                    <li>How and why we process it</li>
                                    <li>How we keep it secure</li>
                                    <li>Your legal rights regarding your personal data</li>
                                </ul>
                                <p>This Notice should be read together with Koova’s <em>Terms & Conditions</em> and <em>Cookies Notice</em>.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    2. Who We Are
                                </h2>
                                <p>Koova is a digital marketplace platform that connects sellers and buyers of goods and services.</p>
                                <p>Koova may also provide logistics, delivery coordination, and payment facilitation services through third-party partners.</p>
                                <p>The Koova entity operating the website or mobile application is the data controller responsible for your personal data, unless otherwise stated.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    3. Personal Data We Collect
                                </h2>
                                <p>“Personal data” means any information that can directly or indirectly identify an individual.</p>
                                <p>We collect personal data to operate, improve, and personalize our Platform, comply with legal obligations, and prevent fraud.</p>

                                <p><strong>A. Information You Provide to Us</strong><br />
                                    This includes:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Full name</li>
                                    <li>Email address</li>
                                    <li>Phone number</li>
                                    <li>Physical and delivery addresses</li>
                                    <li>Date of birth or age range</li>
                                    <li>Account login credentials</li>
                                    <li>Payment and billing information</li>
                                    <li>Responses to surveys or promotions</li>
                                    <li>Customer support communications</li>
                                    <li>Marketing preferences</li>
                                </ul>
                                <p>You provide this information when you register, create listings, make purchases, communicate with other users, or contact support.</p>

                                <p><strong>B. Information Collected Automatically or From Third Parties</strong><br />
                                    We may collect information about:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Your device, browser, operating system, and IP address</li>
                                    <li>Unique device identifiers</li>
                                    <li>Pages viewed, searches performed, and listings interacted with</li>
                                    <li>Purchase history and transaction activity</li>
                                    <li>Time spent on the Platform</li>
                                    <li>User-generated content you post</li>
                                    <li>Social media identifiers where you interact with Koova through third-party platforms</li>
                                </ul>
                                <p>We may also receive information from:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Payment processors</li>
                                    <li>Delivery and logistics partners</li>
                                    <li>Advertising and analytics providers</li>
                                    <li>Fraud-prevention services</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    4. Cookies and Tracking Technologies
                                </h2>
                                <p>Koova uses cookies and similar technologies to:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Enable core site functionality</li>
                                    <li>Improve performance and user experience</li>
                                    <li>Analyze usage patterns</li>
                                    <li>Provide personalized content and advertising (where permitted by law)</li>
                                </ul>
                                <p>You can manage cookies through your browser settings.<br />
                                    Further details are provided in the <em>Koova Cookies Notice</em>.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    5. How We Use Your Personal Data
                                </h2>
                                <p>We use your personal data to:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Register and manage user accounts</li>
                                    <li>Facilitate transactions between buyers and sellers</li>
                                    <li>Process payments and deliveries</li>
                                    <li>Provide customer support</li>
                                    <li>Communicate service updates and notifications</li>
                                    <li>Enable promotions, competitions, and surveys</li>
                                    <li>Improve Platform features and performance</li>
                                    <li>Detect and prevent fraud, abuse, and security incidents</li>
                                    <li>Comply with legal and regulatory obligations</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    6. Legal Basis for Processing
                                </h2>
                                <p>We process personal data only where permitted by law, including:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Consent</strong> – where you have explicitly agreed (e.g., marketing communications)</li>
                                    <li><strong>Contractual necessity</strong> – to provide services you request</li>
                                    <li><strong>Legitimate interests</strong> – to operate, secure, and improve the Platform</li>
                                    <li><strong>Legal obligations</strong> – to comply with applicable laws and regulations</li>
                                </ul>
                                <p>You may withdraw consent at any time where consent is the legal basis.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    7. Sharing of Personal Data
                                </h2>
                                <p>We may share your personal data with:</p>

                                <p><strong>A. Service Providers</strong><br />
                                    Third parties that assist with:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Payment processing</li>
                                    <li>Deliveries and logistics</li>
                                    <li>Data analytics</li>
                                    <li>Marketing and communications</li>
                                    <li>Customer support</li>
                                    <li>Fraud detection</li>
                                </ul>

                                <p><strong>B. Marketplace Participants</strong><br />
                                    Where necessary to complete transactions between buyers and sellers.</p>

                                <p><strong>C. Legal and Regulatory Authorities</strong><br />
                                    Where required by law or to protect legal rights.</p>

                                <p><strong>D. Business Transfers</strong><br />
                                    In the event of a merger, acquisition, or asset sale.</p>

                                <p>All third parties are required to process your data securely and only for authorized purposes.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    8. International Data Transfers
                                </h2>
                                <p>Your personal data may be transferred to and processed in countries outside your jurisdiction.</p>
                                <p>Where this occurs, Koova implements appropriate safeguards to ensure your data receives adequate protection in accordance with applicable law.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    9. Data Retention
                                </h2>
                                <p>We retain personal data only as long as necessary for:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Active user accounts</li>
                                    <li>Legal, regulatory, or contractual obligations</li>
                                    <li>Resolving disputes</li>
                                    <li>Preventing fraud</li>
                                </ul>
                                <p>When no longer required, data is securely deleted or anonymized.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    10. Data Security
                                </h2>
                                <p>Koova implements technical and organizational measures to protect personal data from:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Unauthorized access</li>
                                    <li>Loss</li>
                                    <li>Misuse</li>
                                    <li>Alteration</li>
                                    <li>Disclosure</li>
                                </ul>
                                <p>Access to personal data is limited to authorized personnel and partners under confidentiality obligations.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    11. Your Legal Rights
                                </h2>
                                <p>Subject to applicable law, you may have the right to:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Access your personal data</li>
                                    <li>Correct inaccurate data</li>
                                    <li>Request deletion of your data</li>
                                    <li>Object to or restrict processing</li>
                                    <li>Withdraw consent</li>
                                    <li>Request data portability</li>
                                    <li>Unsubscribe from marketing communications</li>
                                </ul>
                                <p>You may close your account to request permanent deletion, subject to legal retention requirements.<br />
                                    We may request identity verification before fulfilling certain requests.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    12. Contact and Data Controller
                                </h2>
                                <p>For questions, concerns, or to exercise your data protection rights, contact:</p>
                                <p><strong>Email:</strong> <a href="mailto:support@koova.com" className="text-secondary hover:underline">support@koova.com</a><br />
                                    <strong>Subject line:</strong> Data Privacy Request</p>
                                <p>Koova will investigate and respond to verified requests within reasonable timelines.</p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    13. Related Policies
                                </h2>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><em>Cookies Notice</em></li>
                                    <li><em>Terms & Conditions</em></li>
                                    <li><em>Returns & Refunds Policy</em></li>
                                    <li><em>Store Credit Terms</em></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}

export default PolicyPage